import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, DollarSign, Layers, MapPin, Target } from 'lucide-react';
import { getCriteriaForType } from '@/utils/criteria';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyAdDetailPage = () => {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ad, setAd] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchAdAndProposals();
    } catch (error) {
      toast.error('Connexion requise');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdAndProposals = async () => {
    try {
      const adResponse = await axios.get(`${API}/acquirer-ads/${adId}`, { withCredentials: true });
      setAd(adResponse.data);

      const propsResponse = await axios.get(`${API}/proposals/received`, { withCredentials: true });
      const adProposals = propsResponse.data.filter(p => p.acquirer_ad_id === adId);
      setProposals(adProposals);
    } catch (error) {
      console.error(error);
    }
  };

  if (loading || !ad) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  const criteriaCategories = getCriteriaForType(ad.property_type);

  return (
    <div className="min-h-screen bg-background">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => {}}
        onLogout={async () => {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          navigate('/');
          window.location.reload();
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Button onClick={() => navigate('/mon-compte')} variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border p-8 mb-8">
              <h2 className="font-display text-2xl font-bold text-foreground mb-6">Mon annonce</h2>
              
              <h3 className="text-xl font-bold text-blue-600 mb-3">{ad.title}</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Type</p>
                  <p className="font-bold text-gray-800">{ad.property_type} {ad.rooms > 0 && `${ad.rooms} pièces`}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Budget max</p>
                  <p className="font-bold text-gray-800">{ad.max_budget.toLocaleString('fr-FR')} €</p>
                </div>
                {ad.min_surface && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Surface</p>
                    <p className="font-bold text-gray-800">{ad.min_surface}-{ad.max_surface || ad.min_surface}m²</p>
                  </div>
                )}
              </div>

              {ad.location_data?.address && (
                <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-gray-600 mb-1">📍 Zone de recherche</p>
                  <p className="text-sm font-semibold text-gray-800">{ad.location_data.address}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Rayon : {ad.location_data.radius >= 1000 ? `${(ad.location_data.radius/1000).toFixed(1)} km` : `${ad.location_data.radius} m`}
                  </p>
                </div>
              )}

              {ad.criteria && Object.keys(ad.criteria).some(k => ad.criteria[k]?.length > 0) && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">🎯 Critères recherchés :</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(ad.criteria).map(([catKey, values]) => 
                      values?.map((val, i) => {
                        const cat = criteriaCategories[catKey];
                        const opt = cat?.options.find(o => o.value === val);
                        return opt ? (
                          <span key={`${catKey}-${i}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                            ✓ {opt.label}
                          </span>
                        ) : null;
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-blue-600">📬 Propositions reçues</h2>
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                  {proposals.length}
                </span>
              </div>

              {proposals.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-sm">Aucune proposition pour cette annonce</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {proposals.map((prop) => (
                    <div
                      key={prop.proposal_id}
                      onClick={() => navigate(`/proposal/${prop.proposal_id}`)}
                      className={`bg-white border rounded-xl p-4 cursor-pointer hover:shadow-lg transition-all hover:border-blue-400 ${
                        prop.status === 'deleted' ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {prop.match_score && prop.status !== 'deleted' && (
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                            prop.match_score >= 80 ? 'bg-green-100 text-green-800' :
                            prop.match_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {prop.match_score >= 80 ? '🎯' : '✓'} Match: {prop.match_score}%
                          </span>
                        )}
                        {prop.status === 'deleted' && (
                          <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                            🗑️ Archivée
                          </span>
                        )}
                      </div>
                      {prop.seller_name && (
                        <p className="text-sm text-gray-600 mb-2">
                          👤 Proposé par : <span className="font-semibold text-gray-800">{prop.seller_name}</span>
                        </p>
                      )}
                      <h3 className="font-bold text-gray-800 mb-2">
                        {prop.property_type} - {prop.property_address}
                      </h3>
                      <p className="text-lg font-bold text-blue-600 mb-2">
                        💰 {prop.price.toLocaleString('fr-FR')} €
                      </p>
                      <div className="flex gap-4 text-sm text-gray-600 mb-3">
                        <span>📐 {prop.surface} m²</span>
                        {prop.rooms && <span>🚪 {prop.rooms} pièces</span>}
                      </div>
                      
                      {prop.photos && prop.photos.length > 0 && (
                        <div className="flex gap-2 mb-3 overflow-x-auto">
                          {prop.photos.slice(0, 3).map((photo, i) => {
                            const photoUrl = photo.startsWith('http') ? photo : `https://maison-antibes.preview.emergentagent.com${photo}`;
                            return (
                              <img
                                key={i}
                                src={photoUrl}
                                alt={`Photo ${i+1}`}
                                className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                onError={(e) => {
                                  console.log('Photo error:', photoUrl);
                                  e.target.style.display = 'none';
                                }}
                              />
                            );
                          })}
                        </div>
                      )}
                      
                      <p className="text-xs text-blue-600 font-semibold">
                        → Cliquez pour voir détails et discuter
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAdDetailPage;
